<template>
  <div class="relative">
    <b-table
      :items="dataFetcher.data"
      :fields="fields"
      :empty-text="dataFetcher.isLoading ? '' : 'Tidak ada data yang ditampilkan.'"
      show-empty
      responsive
      class="!max-h-[50vh] !mb-0"
      sticky-header
      @scroll.native="handleScroll"
    >
      <template #cell(order_date)="data">
        <p class="mb-0">
          {{ YEAR_MONTH_DAY(data.value) }}
        </p>
        <p class="text-[#828282] mb-0">
          {{ TIME(data.value) }}
        </p>
      </template>
      <template #cell(partner_name)="data">
        <p class="mb-0">
          {{ data.value }}
        </p>
        <p class="text-[#828282] mb-0">
          {{ data.item.shop_name }}
        </p>
      </template>
      <template #cell(marketplace_order_id)="data">
        <div class="flex items-center gap-[8px]">
          <b-img
            :src="getMarketplaceLogo(data.item.marketplace)"
            fluid
            class="w-[20px]"
          />
          <p class="mb-0">
            {{ data.value }}
          </p>
        </div>
      </template>
      <template #cell(status)="data">
        <StatusOrderPill
          :status="data.value"
          :mapping="statusMapping"
        />
      </template>
      <template #cell(id)="data">
        <router-link
          :to="{ name: 'kompack-total-nilai-transaksi-fulfillment-marketplace-detail', params: { id: data.value } }"
          class="text-[#F95031] hover:underline"
        >
          Lihat Detail
        </router-link>
      </template>
    </b-table>
    <div
      v-if="dataFetcher.isLoading"
      class="absolute bottom-0 left-0 right-0 flex justify-center items-center p-[20px] bg-white bg-opacity-90"
    >
      <div class="flex items-center gap-2">
        <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
        <span class="text-sm text-gray-600">Memuat data...</span>
      </div>
    </div>
  </div>
</template>

<script>
import { createPaginatedFetcher } from '@/@core/libs/fetch/usePaginatedFetcher'
import { YEAR_MONTH_DAY, TIME } from '@/libs/filterDate'
import { fieldsFulfillmentMarketplace } from '../config'
import { statusMapping, MARKETPLACE_PLATFORM } from '../fulfillment/marketplace/config'
import { handleStatusClasses } from '../fulfillment/nonkomship/config'
import StatusOrderPill from '../fulfillment/nonkomship/StatusOrderPill.vue'

/**
   * Component untuk menampilkan data fulfillment marketplace
   * @component FulfillmentMarketplace
   */
export default {
  components: {
    StatusOrderPill,
  },
  props: {
    /** @type {Object} Date range filter { startDate, endDate } */
    period: {
      type: Object,
      required: true,
    },
    /** @type {string|null} Transaction status filter */
    status: {
      type: String,
      default: null,
    },
    /** @type {string} Search keyword (Order No) */
    search: {
      type: String,
      default: '',
    },
    /** @type {number|string|null} Selected warehouse ID */
    warehouseId: {
      type: [Number, String],
      default: null,
    },
    /** @type {number|string|null} Selected partner ID */
    partnerId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      dataFetcher: null,
      statusMapping,
    }
  },
  watch: {
    status() {
      this.handleRefreshData()
    },
    search() {
      this.debouncedRefresh()
    },
    warehouseId() {
      this.handleRefreshData()
    },
    partnerId() {
      this.handleRefreshData()
    },
  },
  created() {
    this.initializeFetcher()
    this.fields = fieldsFulfillmentMarketplace
    this.debouncedRefresh = _.debounce(this.handleRefreshData, 500)
  },
  mounted() {
    this.handleRefreshData()
  },
  beforeDestroy() {
    if (this.debouncedRefresh && this.debouncedRefresh.cancel) {
      this.debouncedRefresh.cancel()
    }
  },
  methods: {
    YEAR_MONTH_DAY,
    TIME,
    handleStatusClasses,
    /**
     * Initialize paginated fetcher for Marketplace fulfillment orders
     */
    initializeFetcher() {
      const fnFetch = ({
        offset, limit, start_date, end_date, status, search, warehouse_id: warehouseId, partner_id: partnerId,
      }) => this.$http_new.get('/komship/api/v1/admin/marketplace-orders/summaries', {
        params: {
          start_date,
          end_date,
          offset,
          limit,
          status,
          marketplace_order_id: search || undefined,
          warehouse_id: warehouseId || undefined,
          partner_id: partnerId || undefined,
        },
      }).then(res => {
        const { data } = res.data
        return {
          data,
        }
      }).catch(() => ({
        data: [],
      }))

      this.dataFetcher = createPaginatedFetcher(fnFetch, 0, 50)
    },
    /**
     * Refresh data with current filters, search query, and date period
     */
    async handleRefreshData() {
      if (this.debouncedRefresh && this.debouncedRefresh.cancel) {
        this.debouncedRefresh.cancel()
      }
      await this.dataFetcher.fetchData({
        reset: true,
        options: {
          start_date: YEAR_MONTH_DAY(this.period.startDate),
          end_date: YEAR_MONTH_DAY(this.period.endDate),
          status: this.status || undefined,
          search: this.search || undefined,
          warehouse_id: this.warehouseId || undefined,
          partner_id: this.partnerId || undefined,
        },
      })
    },
    /**
     * Handle scroll for infinite loading
     * @param {Event} event
     */
    handleScroll(event) {
      if (event.target.scrollTop + event.target.clientHeight + 500 >= event.target.scrollHeight) {
        if (this.dataFetcher.data.length <= 0) return
        this.dataFetcher.fetchData()
      }
    },
    /**
     * Get logo asset path for marketplace platform
     * @param {string} marketplace
     * @returns {string}
     */
    getMarketplaceLogo(marketplace) {
      if (!marketplace) return MARKETPLACE_PLATFORM.kompack.logo
      return MARKETPLACE_PLATFORM[marketplace]?.logo || MARKETPLACE_PLATFORM.kompack.logo
    },
  },
}
</script>
